/* eslint-disable no-underscore-dangle */
import useOrganization from "@hooks/useOrganization";
import _ from "lodash";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { analyticsFilter } from "@queries/AnalyticsFilter";
import { useMemo } from "react";
import useInitialUserInfo from "@hooks/useInitialUserInfo";
import { useAnalyticsRuleData } from "@hooks/analytics/useAnalyticsRuleData";
import {
  AnalyticsFilterRequestDto,
  BaseGetAnalyticsFilterResponseDto,
  GetAnalyticsFilterResponseDto
} from "@open-api";
import {
  AI_TYPE,
  ANALYTICS_CHART_TYPE,
  CALENDAR_MODE_RANGE_TYPE,
  ChartTypes,
  WIDGET_TYPE
} from "@resources/Constants";
import { CalendarModeRangeType, WidgetType } from "@custom-types/Types";
import { getRuleType } from "@hooks/analytics/useLocationRuleGroupByRuleType";
import moment from "moment";
import { DateTime } from "@components/common/calendar/date-range-picker/Interfaces";
import { getDateTime } from "@utils/Widget";
import useSetting from "@hooks/useSetting";
import { useGetPortalDevicesQuery } from "../device/DeviceQuery";
import { keys } from "./keys";

export type RuleType = {
  ruleType: string;
  deviceId: string;
  channel: string;
  ruleName: string;
};

export type Order = GetAnalyticsFilterResponseDto["filterList"][0]["order"][0];
export type ObjectType =
  GetAnalyticsFilterResponseDto["filterList"][0]["objectType"];

export type FilterType = {
  updatedAt?: string;
  baseTime:
    | {
        start: string;
        end: string;
        mode: CalendarModeRangeType;
      }
    | undefined;
  rules: string[] | undefined;
  filterName: string;
  _id?: string;
  order: Order[];
  objectType: ObjectType;
  timeZone?: string;
};

// when single location, ignore people/vehicle occupancy and shopping cart in use
export const REG_SINGLE_LOCATION_IGNORE = /occupancy|inuse/i;

export const ANALYTICS_BASE_TIME = moment()
  .subtract(1, "d")
  .startOf("d")
  .toISOString(true);

export const BASE_TIME = moment().startOf("d").toISOString(true);

export const ANALYTICS_DEFAULT_DATE_TIME: DateTime = {
  ...getDateTime(
    CALENDAR_MODE_RANGE_TYPE.LAST_7_DAYS,
    "",
    undefined,
    ANALYTICS_BASE_TIME
  ),
  mode: "custom"
};

export const ANALYTICS_REPORT_DEFAULT_DATE_TIME: DateTime = {
  ...getDateTime(
    CALENDAR_MODE_RANGE_TYPE.LAST_90_DAYS,
    "",
    undefined,
    ANALYTICS_BASE_TIME
  ),
  mode: CALENDAR_MODE_RANGE_TYPE.LAST_90_DAYS
};

export const toChartType = (orderType: Order) => {
  return _.snakeCase(
    orderType
  ).toUpperCase() as keyof typeof ANALYTICS_CHART_TYPE;
};

export const chartTypeToWidgetType = (
  chartType: keyof typeof ANALYTICS_CHART_TYPE
): WidgetType => {
  switch (chartType) {
    case ANALYTICS_CHART_TYPE.VEHICLE_QUEUES:
      return WIDGET_TYPE.VEHICLE_QUEUE;
    case ANALYTICS_CHART_TYPE.PEOPLE_QUEUES:
      return WIDGET_TYPE.CHECKOUT_QUEUE;
    case ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES:
      return WIDGET_TYPE.SHOPPING_CART_QUEUE_MANAGEMENT;
    default:
      return chartType as WidgetType;
  }
};

export const toOrderType = (chartType: keyof typeof ANALYTICS_CHART_TYPE) => {
  return _.camelCase(chartType) as Order;
};

export const DEFAULT_ORDER = Object.keys(ANALYTICS_CHART_TYPE).map((v) =>
  toOrderType(v as keyof typeof ANALYTICS_CHART_TYPE)
);

export const getDefaultChartOrder = (rules: FilterType["rules"]) => {
  const ruleTypes = _.compact(
    (rules ?? []).map((v: string) => v.split(":")[0])
  ) as (keyof typeof AI_TYPE)[];

  return DEFAULT_ORDER.filter((orderType: Order) => {
    const ruleType = getRuleType(toChartType(orderType));
    if (ruleType) {
      return ruleTypes.includes(ruleType);
    }

    return false;
  });
};

export const toOrderInUse = (order: Order[]) => {
  return _.filter(
    order,
    (type) => _.snakeCase(type).toUpperCase() in ANALYTICS_CHART_TYPE
  );
};

export const toFilter = (resp: BaseGetAnalyticsFilterResponseDto) => {
  const isEmptyObjectType = !resp.objectType;
  return {
    _id: resp._id,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // API scheme is not updated updatedAt key yet
    updatedAt: resp.updatedAt,
    filterName: resp.filterName,
    baseTime: {
      start: resp.startDateTime,
      end: resp.endDateTime,
      mode: "custom" as BaseGetAnalyticsFilterResponseDto["calenderMode"]
    },
    rules: (() => {
      // swagger scheme is invalid, so force cast
      return (resp.rules as unknown as RuleType[])
        .filter((v) => {
          if (isEmptyObjectType) {
            // [workaround] handling users of previous versions
            if (
              v.ruleType === AI_TYPE.PEOPLE_COUNT ||
              v.ruleType === AI_TYPE.QUEUE_MANAGEMENT ||
              v.ruleType === AI_TYPE.SLIP_AND_FALL ||
              v.ruleType === AI_TYPE.CROWD_COUNT
            ) {
              return true;
            }
            return false;
          }

          return true;
        })
        .map((rule) => {
          return [
            rule.ruleType,
            rule.deviceId,
            rule.channel,
            rule.ruleName
          ].join(":");
        });
    })(),
    objectType: resp.objectType ?? "People",
    order: (() => {
      // if all rule is invalid, return empty array
      // ex) if camera that have rule is unclaimed, return empty array
      if (resp.rules.length === 0) {
        return [];
      }

      if (_.isEmpty(resp.order)) {
        return DEFAULT_ORDER;
      }

      // if, objectType is undefined, use default objectType ( "People" )
      // and filtered chart type by objectType
      if (isEmptyObjectType) {
        // [workaround] handling users of previous versions
        return _.filter(toOrderInUse(resp.order), (type) => {
          return (
            type === ChartTypes.cumulativePeopleCount ||
            type === ChartTypes.peopleOccupancy ||
            type === ChartTypes.peopleQueues ||
            type === ChartTypes.slipAndFall ||
            type === ChartTypes.crowdCounting
          );
        });
      }

      return toOrderInUse(resp.order);
    })(),
    timeZone: resp.timeZone
  };
};

export const useGetAnalyticsFilterQuery = () =>
  //   options?: UseQueryOptions<GetChartResponse>
  {
    const { orgId } = useOrganization();
    const { data: settingData } = useSetting(orgId);
    const {
      isLoading: isLoadingDevice,
      isInitialLoading: isInitialLoadingDevice
    } = useGetPortalDevicesQuery(
      {
        orgId,
        sightmindStatus: true
      },
      { enabled: !!orgId }
    );

    // prefetch for cache
    const { originData, isLoading: isLoadingRule } = useAnalyticsRuleData(
      { inValidateCacheOnUnmount: true },
      {
        refetchOnMount: true,
        cacheTime: 0,
        staleTime: 0
      }
    );

    const {
      data: filterData,
      isLoading: isLoadingFilter,
      isInitialLoading: isInitialLoadingFilter
    } = useQuery(
      keys.getFilter(orgId),
      () =>
        analyticsFilter.get({
          path: {
            orgId
          }
        }),
      {
        enabled: !!orgId,
        cacheTime: 0,
        staleTime: 0
      }
    );

    const isLoading = isLoadingDevice || isLoadingFilter || isLoadingRule;
    const isInitialLoading =
      isInitialLoadingDevice || isInitialLoadingFilter || isLoadingRule;
    // [TODO] remove later, the type defined in swagger is incorrect.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const data: FilterType[] = useMemo(() => {
      if (filterData && originData) {
        const portalRuleList = _.compact(
          _.flatMap(originData, (origin) => origin.children).map((v) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const metaData = v.metaData;
            if (metaData) {
              const { channel, deviceId, ruleName, type } = metaData;
              return { channel, deviceId, ruleName, type };
            }
            return null;
          })
        );

        const validFilterList = _.map(filterData.filterList, (filter) => {
          return {
            ...filter,
            rules: (filter.rules as unknown as RuleType[]).filter((rule) => {
              return (
                portalRuleList.findIndex((pr) => {
                  return (
                    pr.deviceId === rule.deviceId &&
                    pr.channel === rule.channel &&
                    pr.ruleName === rule.ruleName &&
                    pr.type === rule.ruleType
                  );
                }) > -1
              );
            }),
            timeZone: filter.timeZone || settingData?.dateTime?.timezone,
            startDateTime: moment(filter.startDateTime)
              .clone()
              .tz(
                filter.timeZone || (settingData?.dateTime?.timezone as string)
              )
              .toISOString(true),
            endDateTime: moment(filter.endDateTime)
              .clone()
              .tz(
                filter.timeZone || (settingData?.dateTime?.timezone as string)
              )
              .toISOString(true)
          };
        });
        return _.map(validFilterList, toFilter);
      }

      return [];
    }, [filterData, originData]);

    return {
      data,
      isLoading,
      isInitialLoading
    };
  };

export const useUpdateFilterNameNotSaveMutation = () => {
  const { orgId } = useOrganization();
  const queryClient = useQueryClient();

  const mutateAsync = async (params: {
    filterId: string;
    filterName: string;
  }) => {
    queryClient.setQueryData<GetAnalyticsFilterResponseDto>(
      keys.getFilter(orgId),
      (prev) => {
        if (!prev) {
          return prev;
        }

        const { filterId, filterName } = params;
        const newFilter = {
          ...prev,
          filterList: prev.filterList.map((filter) => {
            if (filter._id === filterId) {
              return { ...filter, filterName };
            }
            return filter;
          })
        };
        return newFilter;
      }
    );
  };

  return { mutateAsync };
};

export const useUpdateAnalyticsFilterMutation = () => {
  const { orgId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation(
    (
      params: AnalyticsFilterRequestDto & {
        filterId: string;
      }
    ) => {
      const { filterId, ...body } = params;
      return analyticsFilter.put({
        path: { orgId, filterId },
        body
      });
    },
    {
      onSuccess: (_, params) => {
        queryClient.setQueryData<GetAnalyticsFilterResponseDto>(
          keys.getFilter(orgId),
          (prev) => {
            if (!prev) {
              return prev;
            }

            const { filterId, ...req } = params;
            const changedFilter = prev.filterList
              .filter((filter) => filter._id === filterId)
              .map(() => ({ ...req, _id: filterId }))[0];

            const newFilter = {
              ...prev,
              filterList: [
                ...prev.filterList.map((filter) => {
                  if (filter._id === filterId) {
                    return changedFilter;
                  }
                  return filter;
                })
              ]
            };
            return newFilter;
          }
        );
      }
    }
  );
};

export const useCreateAnalyticsFilterMutation = () => {
  const { orgId } = useOrganization();
  const queryClient = useQueryClient();
  const { data: user } = useInitialUserInfo(orgId, {
    staleTime: 1000 * 60 * 60,
    cacheTime: 1000 * 60 * 60,
    refetchOnMount: true
  });

  return useMutation(
    (req: AnalyticsFilterRequestDto) => {
      return analyticsFilter.post({
        path: {
          orgId
        },
        body: req
      });
    },
    {
      onSuccess: (resp: BaseGetAnalyticsFilterResponseDto) => {
        queryClient.setQueryData<GetAnalyticsFilterResponseDto>(
          keys.getFilter(orgId),
          (prev) => {
            if (!prev) {
              return {
                orgId,
                userId: user?.userId ?? "",
                filterList: [resp as BaseGetAnalyticsFilterResponseDto]
              };
            }
            const newFilter = {
              ...prev,
              filterList: [...prev.filterList, resp]
            };

            return newFilter;
          }
        );
      }
    }
  );
};

export const useDeleteAnalyticsFilterMutation = () => {
  const { orgId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation(
    (filterId: string) => {
      return analyticsFilter.delete({
        path: {
          orgId,
          filterId
        }
      });
    },
    {
      onSuccess: (_, params) => {
        queryClient.setQueryData<GetAnalyticsFilterResponseDto>(
          keys.getFilter(orgId),
          (prev) => {
            if (!prev) {
              return prev;
            }

            const filterId = params;
            const newFilter = {
              ...prev,
              filterList: prev.filterList.filter(
                (filter) => filter._id !== filterId
              )
            };
            return newFilter;
          }
        );
      }
    }
  );
};
